import React, { useEffect, useRef, useState } from 'react';

// ─── Shared checkbox list ──────────────────────────────────────────────────
// Used by the toolbar picker below AND by the export modal, so the two always
// present the same column set the same way.
//
// `selected` is a Set of column keys. `onChange` receives the next Set.
// When `requireOne` is true the last remaining checkbox can't be unticked —
// a grid with zero columns is not a useful state to be able to reach.
export function ColumnCheckList({ columns, selected, onChange, requireOne = false }) {
  function toggle(key) {
    const next = new Set(selected);
    if (next.has(key)) {
      if (requireOne && next.size <= 1) return;
      next.delete(key);
    } else {
      next.add(key);
    }
    onChange(next);
  }

  return (
    <div className="col-check-list">
      {columns.map((c) => {
        const checked = selected.has(c.key);
        const locked = requireOne && checked && selected.size <= 1;
        return (
          <label
            key={c.key}
            className={'col-check-item' + (locked ? ' col-check-item--locked' : '')}
            title={locked ? 'At least one column must stay visible' : c.title}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={locked}
              onChange={() => toggle(c.key)}
            />
            <span className="col-check-label">{c.title}</span>
          </label>
        );
      })}
    </div>
  );
}

// ─── Toolbar column picker ─────────────────────────────────────────────────
// Replaces the old "Additional fields" checkbox. `defaultKeys` is the core
// column set (what the grid showed before any explicit choice) — the "Default"
// quick action restores it, which is exactly the old checkbox's OFF state,
// while "All" is its ON state.
export default function ColumnPicker({ columns, selected, onChange, defaultKeys }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onEsc(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div className="bom-col-picker-wrap" ref={wrapRef}>
      <button
        type="button"
        className={'bom-col-picker-btn' + (open ? ' is-open' : '')}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="bom-col-picker-glyph" aria-hidden="true">▦</span>
        <span>Columns</span>
        <span className="bom-col-picker-count">
          {selected.size}/{columns.length}
        </span>
      </button>

      {open && (
        <div className="bom-col-picker-panel">
          <div className="bom-col-picker-head">
            <span className="bom-col-picker-title">Visible columns</span>
            <span className="bom-col-picker-actions">
              <button
                type="button"
                onClick={() => onChange(new Set(defaultKeys))}
                disabled={!defaultKeys?.length}
              >
                Default
              </button>
              <button
                type="button"
                onClick={() => onChange(new Set(columns.map((c) => c.key)))}
              >
                All
              </button>
            </span>
          </div>
          <ColumnCheckList
            columns={columns}
            selected={selected}
            onChange={onChange}
            requireOne
          />
        </div>
      )}
    </div>
  );
}
